import type { ApiResult } from './client';
import { del, get, post } from './client';
import { strategyListSchema, strategySchema } from './_schemas';
import { z } from 'zod';

import type { Strategy } from '@/types/api';

// Operator-scoped list. The endpoint returns denormalized counts
// (subscriptions, signals, last_signal_at) so the page doesn't N+1 across
// signals/assignments. Older fields tolerated; new optional fields surfaced.
export const listStrategies = (signal?: AbortSignal): Promise<ApiResult<Strategy[]>> =>
  get('/admin/strategies', strategyListSchema, { signal }) as Promise<ApiResult<Strategy[]>>;

/**
 * A strategy's DECLARED parameter spec — drives the dynamic deployment form so
 * each strategy renders its own knobs (no hardcoding). Empty `fields` =
 * free-form. `baseline` are the strategy's default param values.
 */
export const paramFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.string(),                       // 'string'|'number'|'bool'|'enum'|'time' (lenient)
  required: z.boolean().optional().default(false),
  default: z.unknown().nullable().optional(),
  description: z.string().optional().default(''),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  options: z.array(z.string()).optional().default([]),
});
export const paramSpecSchema = z.object({
  strategy_id: z.string(),
  fields: z.array(paramFieldSchema),
  baseline: z.record(z.unknown()).optional().default({}),
  // Declared sizing mode (LOTS | FIXED_QTY | PERCENT_CAPITAL) so the form shows
  // the right sizing knob. Defaults to LOTS for older backends.
  sizing_mode: z.string().optional().default('LOTS'),
});
export type ParamField = z.infer<typeof paramFieldSchema>;
export type ParamSpec = z.infer<typeof paramSpecSchema>;

export const getStrategyParamSpec = (
  strategyId: string,
  signal?: AbortSignal,
): Promise<ApiResult<ParamSpec>> =>
  get(
    `/admin/strategies/${encodeURIComponent(strategyId)}/param-spec`,
    paramSpecSchema,
    { signal },
  ) as Promise<ApiResult<ParamSpec>>;

/**
 * Delete a DORMANT strategy (operator, audited). Server refuses (409) anything
 * with deployments or subscriptions; its own signal history is removed with it.
 */
export const deleteStrategy = (
  strategyId: string,
  reason = 'dormant cleanup',
  signal?: AbortSignal,
): Promise<ApiResult<unknown>> =>
  del(
    `/admin/strategies/${encodeURIComponent(strategyId)}?reason=${encodeURIComponent(reason)}`,
    { signal, retry: { attempts: 1 } },
  );

/** Built-in algorithm classes (shipped in code) — the inverse of delete. */
export const strategyClassSchema = z.object({
  key: z.string(),
  class_path: z.string(),
  registered: z.boolean(),
});
export const strategyClassListSchema = z.array(strategyClassSchema);
export type StrategyClass = z.infer<typeof strategyClassSchema>;

export const listStrategyClasses = (
  signal?: AbortSignal,
): Promise<ApiResult<StrategyClass[]>> =>
  get('/admin/strategy-classes', strategyClassListSchema, { signal }) as Promise<
    ApiResult<StrategyClass[]>
  >;

export const registerStrategy = (
  key: string,
  reason = 'restore catalog entry',
  signal?: AbortSignal,
): Promise<ApiResult<Strategy>> =>
  post('/admin/strategies', strategySchema, { key, reason }, {
    signal, retry: { attempts: 1 },
  }) as Promise<ApiResult<Strategy>>;

/* ============================================================================
 * Tier B5 — strategy runtime control (coming-soon). Implementations live in
 * MSW today; the same signatures will hit FastAPI when the endpoints land.
 * ============================================================================ */
